<!--
  StatementHistory.vue
  콜봇 월 정산 · 정산 탭 2-4 의 "마감 이력" 카드 — 머리에 건수와 마지막 이력(시각 · 동작), 그 아래 표에 모든 이력을 늘 펼쳐 보인다
  (접지 않고 줄이지 않는다).

  열: 시각(KST) | 누가(사람 이름 또는 system) | 동작 | 합계 변화(공급가액, before → after) | 메모.
  동작은 자동 확정 / 자동 검토 필요 / 자동 다시 계산 / 다시 계산 / 저장 / 일회성 항목 / 확정 / 재수정
  (REVISION_ACTION_LABELS). 최근 것이 위다. 응답에 `statement.revisions` 가 없거나 비어 있으면 아무것도
  그리지 않는다(계약 추가 제안 — 없을 때는 이력을 숨긴다).

  요청: ~/md/front/admin-콜봇-월정산-프론트요청.md "화면 명세 2-4"(마감 이력), "요청 계약 4-3" `revisions[]`
  데이터: 부모가 넘기는 상세 `statement`. 이 컴포넌트는 아무것도 부르지 않는다.
-->
<template>
  <div v-if="rows.length" class="st-history">
    <h3 :id="headId" class="st-history__head">
      <span>마감 이력</span>
      <span class="st-history__count">{{ rows.length }}건 · 마지막 {{ rows[0].at }} {{ rows[0].action }}</span>
    </h3>

    <!-- Every entry, always open -->
    <div class="st-history__body" role="region" :aria-labelledby="headId">
      <table class="st-history__table">
        <caption class="st-history__sr">마감 이력(최근 것부터)</caption>
        <thead>
          <tr>
            <th scope="col">시각(KST)</th>
            <th scope="col">누가</th>
            <th scope="col">동작</th>
            <th scope="col">합계 변화(공급가액)</th>
            <th scope="col">메모</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in rows" :key="r.key">
            <td class="st-history__num">{{ r.at }}</td>
            <td>{{ r.by }}</td>
            <td><span class="st-history__action">{{ r.action }}</span></td>
            <td class="st-history__num">{{ r.change }}</td>
            <td class="st-history__memo">{{ r.memo || EMPTY }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance } from 'vue';

import { EMPTY } from '../../service/settlementFormat';
import { revisionRows } from '../../service/settlementRules';

const props = defineProps({
  /** 상세 `statement`(정산 문서가 없으면 null) */
  statement: { type: Object, default: null },
});

const headId = `st-history-${getCurrentInstance()?.uid ?? 0}`;

const rows = computed(() => revisionRows(props.statement));
</script>

<style scoped lang="scss">
@use '../../styles/settlement' as *;

.st-history {
  @include st-card;
  padding: 0;

  &__head {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 6px 8px;
    margin: 0;
    padding: 14px 20px;
    font-size: 15px;
    font-weight: 600;
    color: $st-text;
  }

  &__count {
    @include st-num;
    font-size: 13px;
    font-weight: 400;
    color: $st-muted;
  }

  &__body {
    position: relative;
    padding: 0 0 12px;
    overflow-x: auto;
  }

  &__table {
    width: 100%;
    min-width: 640px;
    border-collapse: collapse;
    font-size: 12.5px;

    th,
    td {
      padding: 7px 10px;
      // !important — CertiAccManage.vue 의 전역 td { border-bottom: transparent !important } 가 td 줄을 지워 항목 칸(th)에만 줄이 남는다
      border-bottom: 1px solid $st-border !important;
      text-align: left;
      vertical-align: top;

      &:first-child {
        padding-left: 20px;
      }
    }

    th {
      background: $st-bg;
      font-size: 12px;
      font-weight: 600;
      color: $st-text-secondary;
      white-space: nowrap;
    }
  }

  &__sr {
    @include st-sr-only;
  }

  &__num {
    @include st-num;
  }

  &__action {
    @include st-pill;
  }

  &__memo {
    color: $st-text-secondary;
    word-break: break-word;
  }
}
</style>
